import { ClassifierData } from 'types/classifiers';
import { CustomFieldDto } from 'types/customFields';
import { EntityTextDto } from 'types/jobPositions';
import { JobRoleCreateDto, JobRoleDetailsDto, JobRoleForm, JobRoleGroupType } from 'types/jobRoles';
import { getOptionsFromClassifier } from 'utils/formUtil';

/** Every language the tenant knows, in code order, each with the record's translation or blanks. */
export const entityTextsForLanguages = (
    classifiers: Record<string, ClassifierData>,
    entityTexts: EntityTextDto[] | null | undefined
): EntityTextDto[] =>
    Array.from(
        new Set(
            classifiers['LANGUAGE']?.values
                ?.map((value) => value.code)
                .toSorted((a: string, b: string) => a.localeCompare(b)) ?? []
        )
    ).map((langCode) => {
        const entityText = entityTexts?.find((el) => el.langCode === langCode);
        return {
            id: entityText?.id ?? null,
            langCode,
            value: entityText?.value ?? '',
            description: entityText?.description ?? '',
            textMale: entityText?.textMale ?? '',
            textFemale: entityText?.textFemale ?? '',
        };
    });

/**
 * The edit form's values for a stored job role; the form adds the custom fields' values itself. Without one (it failed
 * to load) the edit page shows the blank form.
 */
export const jobRoleDtoToForm = (
    jobRole: JobRoleDetailsDto | undefined,
    classifiers: Record<string, ClassifierData>
): JobRoleForm => ({
    name: jobRole?.name ?? '',
    internalName: jobRole?.internalName ?? '',
    abbreviation: jobRole?.abbreviation ?? '',
    orderNum: jobRole?.orderNum ?? null,
    isActive: jobRole?.isActive ?? true,
    excludeFromMinmaxCalculation: jobRole?.excludeFromMinmaxCalculation ?? false,
    groupNameType:
        (
            getOptionsFromClassifier(classifiers['JOB_ROLE_GROUP_TYPE']) as {
                label: string;
                value: JobRoleGroupType;
            }[]
        ).find((val) => val.value === jobRole?.groupNameTypeCode) ?? null,
    entityTexts: entityTextsForLanguages(classifiers, jobRole?.entityTexts),
    jobRoleEmployees:
        jobRole?.jobRoleEmployees?.map((employee) => ({
            id: employee.id,
            name: employee.name,
        })) ?? [],
});

/** The request for the form; no custom fields (a tenant with none defined) leaves the stored values as they are. */
export const jobRoleFormToRequest = (form: JobRoleForm, customFields: CustomFieldDto[] = []): JobRoleCreateDto => ({
    name: form.name,
    internalName: form.internalName,
    abbreviation: form.abbreviation || null,
    orderNum: form.orderNum,
    isActive: form.isActive,
    excludeFromMinmaxCalculation: form.excludeFromMinmaxCalculation,
    groupNameTypeCode: form?.groupNameType?.value ?? JobRoleGroupType.LEGAL,
    entityTexts: form.entityTexts?.filter((el) => !!el.value || (!!el.textMale && !!el.textFemale)) ?? null,
    ...(customFields.length > 0 ? { customFields } : {}),
});

/** The form fields each card of the job role page edits; the employees are read only. */
export const JOB_ROLE_SECTION_FIELDS = {
    'job-role': ['orderNum', 'name', 'internalName', 'abbreviation', 'groupNameType'],
    settings: ['isActive', 'excludeFromMinmaxCalculation'],
    translations: ['entityTexts'],
    'custom-fields': [],
} satisfies Record<string, (keyof JobRoleForm)[]>;

export type JobRoleSectionId = keyof typeof JOB_ROLE_SECTION_FIELDS;
